import { useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import JsPDF from "jspdf";
import { DownloadOutlined } from "@ant-design/icons";
import { useToast } from "../../../contexts/ToastContext";
import useBookingQrCode from "./useBookingQrCode";
import cn from "../../../utilities/tailwind-merge";

const STYLE_OPTIONS = [
    { key: "bold", labelKey: "styleBold" },
    { key: "minimal", labelKey: "styleMinimal" },
];

const FORMAT_OPTIONS = [
    { key: "letter", labelKey: "formatLetter" },
    { key: "instagram", labelKey: "formatInstagram" },
];

const FILE_TYPE_OPTIONS = [
    { key: "pdf", labelKey: "fileTypePdf" },
    { key: "image", labelKey: "fileTypeImage" },
];

const waitForNextFrame = () =>
    new Promise((resolve) => {
        requestAnimationFrame(resolve);
    });

const ToggleGroup = ({ options, value, onChange }) => (
    <div className="flex flex-wrap gap-3">
        {options.map((option) => (
            <button
                key={option.key}
                type="button"
                onClick={() => onChange(option.key)}
                className={cn(
                    "rounded-lg border px-4 py-1.5 text-sm font-medium transition-colors",
                    value === option.key
                        ? "border-[#5D5DF6] bg-[#F0F0FE] text-[#5D5DF6]"
                        : "border-gray-200 text-gray-600 hover:border-gray-300",
                )}>
                {option.label}
            </button>
        ))}
    </div>
);

ToggleGroup.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
    ).isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

const MarketingFlyerCard = ({ bookingUrl, companyName, logoUrl }) => {
    const { t } = useTranslation("shareGrow");
    const toast = useToast();
    const flyerRef = useRef(null);
    const [style, setStyle] = useState("bold");
    const [format, setFormat] = useState("instagram");
    const [fileType, setFileType] = useState("pdf");
    const [downloading, setDownloading] = useState(false);
    const qrDataUrl = useBookingQrCode(bookingUrl);

    // The logo lives on a third-party storage host. When it doesn't send CORS
    // headers, html2canvas can't read its pixels into the exported file (it just
    // silently skips it), so before capturing we probe whether it's readable and,
    // if not, swap to a text badge for the capture only — the on-screen preview
    // keeps showing the real image either way. The probe is awaited at download
    // time (not fired-and-forgotten in an effect) so there's no race between a
    // slow cross-origin request and the user clicking download.
    const [capturing, setCapturing] = useState(false);
    const logoProbeCache = useRef(new Map());

    const isLogoCapturable = (url) => {
        if (!url) return Promise.resolve(true);
        if (logoProbeCache.current.has(url)) return logoProbeCache.current.get(url);

        const probePromise = new Promise((resolve) => {
            const probe = new Image();
            probe.crossOrigin = "anonymous";
            probe.onload = () => resolve(true);
            probe.onerror = () => resolve(false);
            probe.src = url;
        });
        logoProbeCache.current.set(url, probePromise);
        return probePromise;
    };

    const isBold = style === "bold";
    const isInstagram = format === "instagram";
    const showLogoImage = Boolean(logoUrl) && !capturing;

    const handleDownload = async () => {
        if (!flyerRef.current || downloading) return;

        setDownloading(true);
        let needsFallback = false;

        try {
            needsFallback = Boolean(logoUrl) && !(await isLogoCapturable(logoUrl));

            if (needsFallback) {
                setCapturing(true);
                await waitForNextFrame();
                await waitForNextFrame();
            }

            const canvas = await html2canvas(flyerRef.current, {
                scale: 3,
                useCORS: true,
                backgroundColor: isBold ? "#0f172a" : "#ffffff",
            });

            const fileBaseName = `softcar-flyer-${style}-${format}`;

            if (fileType === "image") {
                const link = document.createElement("a");
                link.href = canvas.toDataURL("image/png");
                link.download = `${fileBaseName}.png`;
                link.click();
            } else {
                const pdf = isInstagram
                    ? new JsPDF({ orientation: "portrait", unit: "in", format: [8, 8] })
                    : new JsPDF("p", "in", "letter");
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();
                pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, pageWidth, pageHeight);
                pdf.save(`${fileBaseName}.pdf`);
            }

            toast.success({ message: t("flyerDownloaded") });
        } catch (_error) {
            toast.error({ message: t("flyerGenerationFailed") });
        } finally {
            if (needsFallback) setCapturing(false);
            setDownloading(false);
        }
    };

    const styleOptions = STYLE_OPTIONS.map((option) => ({ key: option.key, label: t(option.labelKey) }));
    const formatOptions = FORMAT_OPTIONS.map((option) => ({ key: option.key, label: t(option.labelKey) }));
    const fileTypeOptions = FILE_TYPE_OPTIONS.map((option) => ({ key: option.key, label: t(option.labelKey) }));

    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <h3 className="text-left text-base font-semibold text-gray-900">{t("yourMarketingFlyer")}</h3>
            <p className="mt-1 text-left text-sm text-gray-500">{t("yourMarketingFlyerDescription")}</p>

            <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:gap-5">
                <div className="flex w-full justify-center lg:w-[260px] lg:shrink-0 lg:justify-start">
                    <div
                        ref={flyerRef}
                        className={cn(
                            "mx-auto flex w-full max-w-[260px] flex-col justify-between overflow-hidden rounded-xl p-5",
                            isInstagram ? "h-[300px]" : "h-[340px]",
                            isBold
                                ? "bg-gradient-to-br from-slate-900 via-slate-800 to-[#2b2b45] text-white"
                                : "border border-gray-200 bg-white text-gray-900",
                        )}>
                        <div className="text-base font-bold">
                            {showLogoImage ? (
                                <img src={logoUrl} alt={companyName || "logo"} className="h-7 max-w-[130px] object-contain" />
                            ) : (
                                <span className={isBold ? "text-white" : "text-gray-900"}>
                                    {companyName || t("title")}
                                </span>
                            )}
                        </div>

                        <div className={cn("text-2xl font-bold leading-tight", isBold ? "text-white" : "text-gray-900")}>
                            {t("flyerHeadline")}
                        </div>

                        <div
                            className={cn(
                                "flex items-center gap-3 rounded-lg p-2",
                                isBold ? "bg-white/95" : "border border-gray-200 bg-gray-50",
                            )}>
                            {qrDataUrl ? (
                                <img src={qrDataUrl} alt="" className="h-10 w-10 shrink-0" />
                            ) : (
                                <div className="h-10 w-10 shrink-0 rounded bg-gray-200" />
                            )}
                            <div className="min-w-0">
                                <p className="text-[10px] font-semibold text-[#5D5DF6]">{t("scanToBook")}</p>
                                <p className="truncate text-[10px] text-gray-600">{bookingUrl}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-4">
                    <div>
                        <p className="mb-3 text-sm font-semibold text-gray-700">{t("style")}</p>
                        <ToggleGroup options={styleOptions} value={style} onChange={setStyle} />
                    </div>

                    <div>
                        <p className="mb-3 text-sm font-semibold text-gray-700">{t("format")}</p>
                        <ToggleGroup options={formatOptions} value={format} onChange={setFormat} />
                    </div>

                    <div>
                        <p className="mb-3 text-sm font-semibold text-gray-700">{t("downloadAs")}</p>
                        <ToggleGroup options={fileTypeOptions} value={fileType} onChange={setFileType} />
                    </div>

                    <button
                        type="button"
                        onClick={handleDownload}
                        disabled={downloading || !bookingUrl}
                        className="mt-auto flex items-center justify-center gap-3 rounded-xl bg-[#5D5DF6] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#4a4af0] disabled:cursor-not-allowed disabled:opacity-60">
                        <DownloadOutlined /> {t("downloadFlyer")}
                    </button>
                </div>
            </div>
        </div>
    );
};

MarketingFlyerCard.propTypes = {
    bookingUrl: PropTypes.string,
    companyName: PropTypes.string,
    logoUrl: PropTypes.string,
};

MarketingFlyerCard.defaultProps = {
    bookingUrl: "",
    companyName: "",
    logoUrl: "",
};

export default MarketingFlyerCard;
